import { useCallback, useEffect, useRef } from 'react';
import { useShortcut } from '../lib/keybindings';

/**
 * Debounced autosave hook. Calls the supplied `flush` after `debounceMs` of inactivity,
 * or immediately on Cmd+S. Also flushes when the page unloads or the hook unmounts with
 * pending changes.
 */
export function useAutosave(
  flush: () => Promise<void> | void,
  debounceMs = 30_000,
) {
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef(false);
  const flushRef = useRef(flush);

  useEffect(() => {
    flushRef.current = flush;
  }, [flush]);

  const doFlush = useCallback(async () => {
    if (!pending.current) return;
    pending.current = false;
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
      timer.current = undefined;
    }
    await flushRef.current();
  }, []);

  const markDirty = useCallback(() => {
    pending.current = true;
    if (timer.current !== undefined) {
      window.clearTimeout(timer.current);
    }
    timer.current = window.setTimeout(() => {
      void doFlush();
    }, debounceMs);
  }, [debounceMs, doFlush]);

  useShortcut({ key: 's', meta: true }, (event) => {
    event.preventDefault();
    void doFlush();
  });

  useEffect(() => {
    const onBeforeUnload = () => {
      if (pending.current) void doFlush();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (pending.current) void doFlush();
    };
  }, [doFlush]);

  return { markDirty, flushNow: doFlush };
}
